import cloneDeep from 'lodash/cloneDeep'
import {
  getNetworkConnectLoSearchResponse,
  getNetworkConnectMyInfoResponse,
  updateNetworkConnectBio,
  waitForNetworkConnectDelay,
} from './network-connect-scenarios'
import {
  completeOnboardingScenarioEnvelope,
  getOnboardingScenarioApiResponse,
  getOnboardingScenarioResponse,
  getOnboardingScenarioSigningUrl,
  submitOnboardingScenarioActivity,
  waitForOnboardingScenarioDelay,
} from './onboarding-scenarios'

const apiStubs = [
  {
    id: 'onboarding.myOnboardingSteps',
    method: 'get',
    path: '/office-activity/my-onboarding-steps',
    delay: 'load',
    response: () => getOnboardingScenarioResponse(),
  },
  {
    id: 'onboarding.myOnboardingAllSteps',
    method: 'get',
    path: '/office-activity/my-onboarding-all-steps',
    delay: 'load',
    response: () => getOnboardingScenarioApiResponse(),
  },
  {
    id: 'onboarding.submitActivity',
    method: 'post',
    pathPattern: /^\/office-activity\/submit\/([^/]+)$/,
    delay: 'submit',
    response: (config) => {
      const activityId = getRequestPath(config).match(/^\/office-activity\/submit\/([^/]+)$/)?.[1]
      return {
        data: submitOnboardingScenarioActivity(activityId),
      }
    },
  },
  {
    id: 'docusign.getSigningUrl',
    method: 'post',
    path: '/docusign/get-signing-url',
    delay: 'signingUrl',
    response: (config) => {
      const body = parseRequestBody(config)
      return getOnboardingScenarioSigningUrl(body.officeActivityListId)
    },
  },
  {
    id: 'docusign.updateEnvelope',
    method: 'post',
    path: '/docusign/update-envelope',
    delay: 'completeEnvelope',
    response: (config) => {
      const body = parseRequestBody(config)
      return {
        data: completeOnboardingScenarioEnvelope(body.officeActivityListId),
      }
    },
  },
  {
    id: 'networkConnect.myInfo',
    method: 'get',
    path: '/referral/my-info',
    delay: () => waitForNetworkConnectDelay('load'),
    response: () => getNetworkConnectMyInfoResponse(),
  },
  {
    id: 'networkConnect.putNetworkBio',
    method: 'put',
    path: '/referral/my-info/network-bio',
    delay: () => waitForNetworkConnectDelay('save'),
    response: (config) => {
      const body = parseRequestBody(config)
      return updateNetworkConnectBio(body.networkBio || '')
    },
  },
  {
    id: 'networkConnect.loSearch',
    method: 'get',
    pathPattern: /^\/referral\/search-lo-users\/[^/]+$/,
    delay: () => waitForNetworkConnectDelay('search'),
    response: () => getNetworkConnectLoSearchResponse(),
  },
]

function getEnabledStubIds() {
  return (process.env.VUE_APP_API_STUBS || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
}

function canUseApiStubs() {
  return process.env.APP_ENV !== 'prod' && getEnabledStubIds().length > 0
}

function getRequestPath(config) {
  const url = config.url || ''

  if (/^https?:\/\//i.test(url)) {
    return new URL(url).pathname
  }

  return url.split('?')[0]
}

function parseRequestBody(config) {
  if (!config.data) {
    return {}
  }

  if (typeof config.data === 'string') {
    try {
      return JSON.parse(config.data)
    } catch (e) {
      console.warn('[api stub] failed to parse request body as JSON, falling back to {}', e)
      return {}
    }
  }

  return config.data
}

function stubMatchesRequest(stub, config, enabledStubIds) {
  const method = (config.method || 'get').toLowerCase()
  const isEnabled = enabledStubIds.includes('*') || enabledStubIds.includes(stub.id)
  const requestPath = getRequestPath(config)
  const pathMatches = stub.pathPattern ? stub.pathPattern.test(requestPath) : stub.path === requestPath

  return isEnabled && stub.method === method && pathMatches
}

function findStubForRequest(config, enabledStubIds) {
  return apiStubs.find((stub) => stubMatchesRequest(stub, config, enabledStubIds))
}

function createStubAdapter(stub) {
  return async (config) => {
    console.info(`[api stub] ${stub.id}`)

    if (typeof stub.delay === 'function') {
      await stub.delay()
    } else {
      await waitForOnboardingScenarioDelay(stub.delay)
    }

    const response = typeof stub.response === 'function' ? await stub.response(config) : stub.response

    return {
      data: cloneDeep(response),
      status: stub.status || 200,
      statusText: stub.statusText || 'OK',
      headers: stub.headers || {},
      config,
      request: null,
    }
  }
}

export default function installApiStubs(axiosInstance) {
  if (!canUseApiStubs()) {
    return
  }

  const enabledStubIds = getEnabledStubIds()

  axiosInstance.interceptors.request.use((config) => {
    const stub = findStubForRequest(config, enabledStubIds)

    if (stub) {
      config.adapter = createStubAdapter(stub)
    }

    return config
  })
}
